import { Utils } from '../../../../../utils/common'
import { logger } from '../../../../../utils/common/logger'
import { DOMUtils } from '../../../../../utils/dom'
import { IdentityEvent } from '../../../../common/events/identity'
import { IdentityScreen } from '../../../enums'
import { PasswordRequirementsList } from '../../../modules/password-requirements-list'
import { LoginService, PostRegistrationActions } from '../services/login-service'
import { LoginRadiusWidget, LRWidgetType } from './base'

export interface IWindow extends Window {
    tp: any
}

declare let window: IWindow

export class RegistrationWidget extends LoginRadiusWidget {
    private static DOM = {
        class: {
            passwordContainer: 'content-loginradius-password'
        }
    }
    private static showNewsletterOptIn: boolean

    constructor(container: string, lrObject: any, showNewsletterOptIn: boolean) {
        super()
        this.widgetType = LRWidgetType.REGISTRATION
        this.lrObject = lrObject
        this.container = container
        RegistrationWidget.showNewsletterOptIn = showNewsletterOptIn
        const resendEmailButton = document.querySelector('.identity__link[type=button]') as HTMLButtonElement
        if (resendEmailButton) {
            resendEmailButton.addEventListener('click', () => {this.resendRegistrationEmail()})
        }
    }

    /**
     * Callback function used by LR widget
     * @param response any
     *
     * reference: https://www.loginradius.com/docs/libraries/js-libraries/js-form-library/
     */
    public async onSuccess(response: any) {
        logger('debug', 'Registration form success', response)
        const token = response.access_token
        // LR returns Data or Profile in the login success response object.
        const data = response.Data || response.Profile

        LoginService.clearErrors()
        LoginService.handleLogin(response)
        const profileData = LoginService.createProfile(data)

        document.dispatchEvent(Utils.event(IdentityEvent.IDENTITY_CHANGE, profileData))

        document.dispatchEvent(new CustomEvent(IdentityEvent.NEW_REGISTRATION, {
            detail: {
                email: profileData.email,
                uuid: profileData.uuid
            }
        }))
        await LoginService.addUserToSocialRegList(profileData.email)

        document.addEventListener(IdentityEvent.IDENTITY_PROFILE_UPDATED, async () => {
            LoginService.sendRegistrationAnalytics(
                response.Profile.RegistrationProvider || 'traditionalRegister',
            )

            await LoginService.setSailthruCookie(profileData.email)

            let redirectUrl = LoginService.getRedirectUrl(location.origin, token)
            const action = LoginService.getPostRegistrationStep(redirectUrl, RegistrationWidget.showNewsletterOptIn)
            if (action === PostRegistrationActions.REDIRECT) {
                location.replace(LoginService.getSSORedirectURL(response.access_token, redirectUrl))
            } else if (action === PostRegistrationActions.SHOW_CHECKOUT) {
                const pianoParams = LoginService.getPianoParams()
                LoginService.showCheckoutModal(pianoParams)
            } else if (action === PostRegistrationActions.SUBSCRIBE) {
                // This is a strange case where we do nothing because there was
                // user_wants to subscribe but with no piano params
                // Not sure if that's even possible but let's keep the way the old code worked in this
                // refactored version.
            } else if (action === PostRegistrationActions.SHOW_NEWSLETTER_OPT_IN) {
                redirectUrl = LoginService.getNewsletterRedirectURL(location.origin)
                location.replace(LoginService.getSSORedirectURL(response.access_token, redirectUrl))
            } else {
               // Success page
               DOMUtils.removeClass(document.body, IdentityScreen.FORM)
               DOMUtils.addClass(document.body, IdentityScreen.SUCCESS)
            }

            // add email address
            const emailAddressEl = document.querySelector('[data-identity-email]')
            if (emailAddressEl) {
                emailAddressEl.innerHTML = profileData.email
            }

            LoginService.setRedirectLink(response.access_token)
            LoginService.setSSOFooterLinks(response.access_token)
        })
    }

    /**
     * Callback function used by LR widget
     * @param errors any
     *
     * reference: https://www.loginradius.com/docs/libraries/js-libraries/js-form-library/
     */
    public onError(errors: any) {
        logger('error', errors)
        // Remove 'loading' class from the submit button and enable it
        const submitButton = document.getElementById('loginradius-submit-register') as HTMLInputElement
        if (submitButton) {
            submitButton.classList.remove('loading')
            submitButton.removeAttribute('disabled')
        }
        LoginService.clearErrors()
        LoginService.showErrors(errors)
        PasswordRequirementsList.showPasswordRequirementsList(RegistrationWidget.DOM.class.passwordContainer)
        LoginService.sendSubmissionErrorAnalytics(errors, 'registration')
    }

    /**
     * Resend email as per user request
     */
    public resendRegistrationEmail() {
        logger('info', 'Email resend requested')
        const registrantEmail = document.querySelector('[data-identity-email]')
        this.lrObject.api.resendEmailVerification({
                email: registrantEmail.innerHTML
            },
            this.onResendEmailSuccess,
            this.onResendEmailError
        )
    }

    /**
     * Callback function used by Resend email function
     * @param response any
     *
     * reference:
     * https://www.loginradius.com/docs/developer/references/javascript-library/form-library/#resend-email-verification
     */
    public onResendEmailSuccess(response: any) {
        logger('info', response)

        // Disable resent email button
        const resendEmailButton = document.querySelector('.identity__link[type=button]') as HTMLButtonElement
        resendEmailButton.setAttribute('disabled', 'disabled')

        // Show success message
        const successMessage = document.createElement('p')
        successMessage.innerHTML = 'We have resent your activation email'
        DOMUtils.addClass(successMessage, 'resend-activation-email__message--success')

        if (resendEmailButton) {
            resendEmailButton.parentElement.after(successMessage)
        }
    }

    /**
     * Callback function used by Resend email function
     * @param errors any
     *
     * reference:
     * https://www.loginradius.com/docs/developer/references/javascript-library/form-library/#resend-email-verification
     */
    public onResendEmailError(errors: any) {
        logger('error', errors)
    }
}
